import React from 'react';
import { Card } from './Card';
import { Text } from '@/components/ui/text/Text';
import { IconScout, IconScoutName } from '@/components/ui/images/IconScout';
import { InfoBubble } from '@/components/ui/tooltips';
import { colors } from '@/assets/styles/colors';


export type IndicatorCardVariant = 'default' | 'primary' | 'important' | 'success' | 'error' | 'alert';

interface IndicatorCardProps {
  label: string;
  value: string | number;
  supportingText?: string;
  icon?: IconScoutName;
  variant?: IndicatorCardVariant;
  padValue?: boolean | number;
  className?: string;
  /** Tooltip mostrado junto al título. */
  tooltipText?: string;
  /** Título centrado mostrado sobre el texto del tooltip. */
  tooltipTitle?: string;
  /** Al informarse, la tarjeta pasa a ser interactiva y se renderiza como botón. */
  onClick?: () => void;
  /**
   * Cuando no hay datos que respalden el valor mostrado (sin corte, corte en recálculo, o el
   * corte no tiene registros), fuerza el color del valor a un gris neutro en vez del color de
   * `variant` y suprime el borde de selección (queda idéntica a una tarjeta no seleccionada) —
   * evita comunicar un estado (ok/error/alerta) sobre un número que en realidad no representa nada.
   */
  muted?: boolean;
  /** Solo aplica en tarjetas interactivas: resalta la tarjeta con el borde de su variante. */
  isSelected?: boolean;
  'data-testid'?: string;
}

const getVariantColor = (variant: IndicatorCardVariant): string => {
  switch (variant) {
    case 'primary':
      return colors.important.main;
    case 'important':
      return colors.important.main;
    case 'success':
      return colors.feedback.success300;
    case 'error':
      return colors.feedback.error300;
    case 'alert':
      return colors.feedback.alert300;
    case 'default':
    default:
      return colors.grays.neutral33;
  }
};

const formatValue = (
  value: string | number,
  padValue?: boolean | number,
): string => {
  if (padValue === false) {
    return String(value);
  }

  const stringValue = String(value);
  const isNumericString = /^\d+$/.test(stringValue);

  if (typeof value !== 'number' && !isNumericString) {
    return stringValue;
  }

  const minDigits = padValue === true || padValue === undefined ? 2 : padValue;
  return stringValue.padStart(minDigits, '0');
};

export const IndicatorCard: React.FC<IndicatorCardProps> = ({
  label,
  value,
  supportingText,
  icon,
  variant = 'default',
  padValue = true,
  className,
  tooltipText,
  tooltipTitle,
  onClick,
  muted = false,
  isSelected = false,
  'data-testid': dataTestId,
}) => {
  const variantColor = muted ? colors.grays.neutral99 : getVariantColor(variant);
  const isInteractive = onClick !== undefined;

  const card = (
    <Card
      elevation={1}
      className={className}
      data-testid={isInteractive && dataTestId ? `${dataTestId}-card` : dataTestId}
      padding="16px"
      style={isInteractive
        ? {
          minWidth: '180px',
          border: `2px solid ${isSelected && !muted ? variantColor : 'transparent'}`,
        }
        : { minWidth: '150px', maxHeight: '125px' }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {/* Label */}
        <Text
          variant="subheader-medium"
          color={colors.primary.dash}
          data-testid={`${dataTestId}-label`}
        >
          {label}
          {tooltipText && (
            // Las tarjetas de indicador suelen ir en la parte superior de la pantalla, donde un
            // tooltip hacia arriba queda fuera del viewport (InfoBubble posiciona por CSS y no
            // se voltea solo). Se abre hacia abajo, que es donde siempre hay espacio.
            <InfoBubble
              text={tooltipText}
              placement="bottom"
              title={tooltipTitle}
              data-testid={dataTestId ? `${dataTestId}-info` : undefined}
            />
          )}
        </Text>

        {/* Indicator Value with optional Icon */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '8px' }}>
          {icon && (
            <IconScout
              name={icon}
              size="lg"
              color={variantColor}
              data-testid={`${dataTestId}-icon`}
            />
          )}
          <Text
            variant="header-4"
            color={variantColor}
            data-testid={`${dataTestId}-value`}
          >
            {formatValue(value, padValue)}
          </Text>
        </div>

        {/* Supporting Text */}
        {supportingText && (
          <Text
            variant="small-regular"
            color={colors.grays.neutral66}
            style={{ fontSize: '10px' }}
            data-testid={`${dataTestId}-supporting`}
          >
            {supportingText}
          </Text>
        )}
      </div>
    </Card>
  );

  if (!isInteractive) {
    return card;
  }

  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={isSelected}
      className="flex-1 min-w-[180px] text-left bg-transparent border-none p-0 cursor-pointer"
      data-testid={dataTestId}
    >
      {card}
    </button>
  );
};
